import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = '3cbed7724b46822431a90b346601ab37';

let isInitialized = false;

/**
 * Initialize Mixpanel SDK
 * Should be called once at app entry point
 * Matches the working implementation from main website
 */
export const initMixpanel = () => {
  if (typeof window === 'undefined') return mixpanel;
  if (isInitialized || window.__MIXPANEL_INIT__) return mixpanel;

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      api_host: 'https://api-eu.mixpanel.com', // CRITICAL: Use EU endpoint
      debug: process.env.REACT_APP_ENV_MODE === 'DEV',
      // Page views are emitted explicitly by AppContent. Automatic history
      // capture would send one-use supplier invitation tokens from /join/:token
      // (and auth redirect query strings) to Mixpanel before React can redact
      // or suppress the route.
      track_pageview: false,
      persistence: 'localStorage',
      autocapture: false, // Disabled - using custom event tracking only
      record_sessions_percent: 100,
      record_heatmap_data: true,
      ip: true,
      save_referrer: true,
      record_mask_text_selector: '',
    });
    window.__MIXPANEL_INIT__ = true;
    isInitialized = true;
    console.log('✅ Mixpanel initialized successfully with EU endpoint');
  } catch (error) {
    console.error('❌ Failed to initialize Mixpanel:', error);
  }

  return mixpanel;
};

export const trackPageView = (pathname) => {
  if (!pathname || typeof window === 'undefined') return;
  try {
    mixpanel.track('$mp_web_page_view', {
      $current_url: `${window.location.origin}${pathname}`,
      current_page_title: document.title,
      current_domain: window.location.hostname,
      current_url_path: pathname,
    });
  } catch (error) {
    console.error('❌ Failed to track Mixpanel page view:', error);
  }
};

/**
 * Identify user with Clerk data
 * Call this when user signs in or when Clerk user data is available
 *
 * @param {Object} user - Clerk user object
 * @param {Object} organization - Clerk organization object (optional)
 */
export const identifyUser = (user, organization = null) => {
  if (!user) {
    console.warn('⚠️ No user data provided for Mixpanel identification');
    return;
  }

  try {
    // Identify user with Clerk user ID
    mixpanel.identify(user.id);

    // Set user properties (for people profile)
    const userProperties = {
      $email: user.primaryEmailAddress?.emailAddress || '',
      $name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      clerkUserId: user.id,
      createdAt: user.createdAt || new Date().toISOString(),
    };

    // Add organization data if available
    if (organization) {
      userProperties.organizationId = organization.id;
      userProperties.organizationName = organization.name;
      userProperties.organizationType = organization.publicMetadata?.type || '';
      // Plan dimension — lets every event be sliced by free-tier vs paid.
      userProperties.plan = organization.publicMetadata?.plan || '';
    }

    // Set people properties (for user profile in Mixpanel People)
    // Using $set to explicitly set properties
    mixpanel.people.set(userProperties);

    // Set properties that should only be set once (like createdAt)
    mixpanel.people.set_once({
      createdAt: userProperties.createdAt,
      clerkUserId: userProperties.clerkUserId,
    });

    // Also register as super properties (will be included in all events)
    mixpanel.register(userProperties);
  } catch (error) {
    console.error('❌ Failed to identify user in Mixpanel:', error);
  }
};

/**
 * Debug function - Get current Mixpanel user properties
 * Call from console: mixpanelDebug.getCurrentUser()
 */
export const getCurrentMixpanelUser = () => {
  try {
    const distinctId = mixpanel.get_distinct_id();
    const props = mixpanel.get_property('$email');
    console.log('Current Mixpanel User:', {
      distinctId,
      registeredProperties: mixpanel.persistence.properties(),
    });
    return mixpanel.persistence.properties();
  } catch (error) {
    console.error('Error getting Mixpanel user:', error);
    return null;
  }
};

/**
 * Track an event in Mixpanel
 *
 * @param {string} eventName - Name of the event to track
 * @param {Object} properties - Additional properties for the event
 */
// Register the active UI language as a super property, so EVERY existing event
// becomes segmentable by locale with no per-event change — the cheapest way to
// answer "is the Spanish funnel worse than the English one?". Called at boot
// and on every locale switch.
export const registerLocale = (locale) => {
  try {
    mixpanel.register({ locale });
  } catch (error) {
    console.error('Failed to register locale super property:', error);
  }
};

// An explicit switch event. A spike of es->en switches is the clearest signal
// that the Spanish build is unusable somewhere.
export const trackLocaleChanged = (from, to) => {
  trackEvent('Locale Changed', { from, to });
};

export const trackEvent = (eventName, properties = {}) => {
  try {
    // Combine event-specific properties with timestamp
    const eventProps = {
      ...properties,
      timestamp: new Date().toISOString(),
    };

    mixpanel.track(eventName, eventProps);
  } catch (error) {
    console.error(`❌ Failed to track event "${eventName}":`, error);
  }
};

/**
 * Free Trial Events
 */

/**
 * Track when user starts free trial (lands on type selection page)
 *
 * @param {Object} user - Clerk user object
 */
export const trackFreeTrialInitialization = (user) => {
  if (!user) {
    console.warn('⚠️ Cannot track Free Trial Initialization - no user data');
    return;
  }

  trackEvent('FreeT: Free Trial Initialization', {
    userId: user.id,
    userEmail: user.primaryEmailAddress?.emailAddress,
    userName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
  });
};

/**
 * Track when user completes onboarding wizard
 *
 * @param {Object} params - Wizard completion parameters
 * @param {string} params.organizationType - Type of organization (declarant/operator)
 * @param {string} params.organizationName - Name of organization
 * @param {string} params.organizationId - Clerk organization ID
 * @param {Array} params.skippedSteps - Array of skipped step numbers
 * @param {Object} params.user - Clerk user object
 */
export const trackWizardCompletion = ({
  organizationType,
  organizationName,
  organizationId,
  skippedSteps = [],
  user,
}) => {
  if (!user || !organizationId) {
    console.warn('⚠️ Cannot track Wizard Completion - missing user or organization data');
    return;
  }

  trackEvent('FreeT: Wizard Completion', {
    organizationType,
    organizationName,
    organizationId,
    skippedSteps,
    totalSkippedSteps: skippedSteps.length,
    userId: user.id,
    userEmail: user.primaryEmailAddress?.emailAddress,
  });
};

/**
 * Track when user views trial expired page
 */
export const trackTrialExpiredView = () => {
  trackEvent('FreeT: Trial Expired Page Viewed');
};

/**
 * Track when user clicks "Book Demo" on trial expired page
 */
export const trackBookDemoClick = () => {
  trackEvent('FreeT: Book Demo Clicked', {
    source: 'trial_expired_page',
  });
};

/**
 * Track when user clicks "Request Extension" on trial expired page
 */
export const trackRequestExtensionClick = () => {
  trackEvent('FreeT: Request Extension Clicked', {
    source: 'trial_expired_page',
  });
};

/**
 * Track when user opens trial extension email modal
 */
export const trackTrialExtensionModalOpened = () => {
  trackEvent('FreeT: Trial Extension Modal Opened');
};

/**
 * Track when user copies email content from modal
 *
 * @param {string} contentType - Type of content copied ('subject' or 'message')
 */
export const trackEmailContentCopied = (contentType) => {
  trackEvent('FreeT: Email Content Copied', {
    contentType,
    source: 'trial_extension_modal',
  });
};

/**
 * Track when user opens email client from modal
 */
export const trackEmailClientOpened = () => {
  trackEvent('FreeT: Email Client Opened', {
    source: 'trial_extension_modal',
  });
};

/**
 * Free-tier Supplier acquisition-funnel Events
 *
 * These instrument the free-tier-supplier journey (invite → signup → activation
 * → upgrade). Distinct `FreeSupplier:` prefix so they never collide with the
 * paid `FreeT:` (Free Trial) funnel above. Each wrapper delegates to trackEvent
 * (never mixpanel.track directly) and console.warns + returns early when key
 * data is missing (mirrors trackFreeTrialInitialization).
 */

/** Supplier opened an invite / self-serve landing (JoinLandingPage). */
export const trackFreeSupplierInviteViewed = ({ declarantName, entry } = {}) => {
  if (!entry) {
    console.warn('⚠️ Cannot track FreeSupplier Invite Viewed - missing entry');
    return;
  }
  trackEvent('FreeSupplier: Invite Link Viewed', {
    declarantName,
    entry,
    source: 'join_landing',
  });
};

/** Supplier reached the first authenticated hop after signup. */
export const trackFreeSupplierSignupStarted = ({ entry } = {}) => {
  if (!entry) {
    console.warn('⚠️ Cannot track FreeSupplier Signup Started - missing entry');
    return;
  }
  trackEvent('FreeSupplier: Signup Started', { entry });
};

/** Supplier finished the operator onboarding wizard on the free tier. */
export const trackFreeSupplierOnboardingCompleted = ({ declarantName, entry } = {}) => {
  if (!entry) {
    console.warn('⚠️ Cannot track FreeSupplier Onboarding Completed - missing entry');
    return;
  }
  trackEvent('FreeSupplier: Onboarding Completed', { declarantName, entry });
};

/** First piece of real data added in /lite (fires once per kind). */
export const trackFreeSupplierFirstDataAdded = ({ kind } = {}) => {
  if (!kind) {
    console.warn('⚠️ Cannot track FreeSupplier First Data Added - missing kind');
    return;
  }
  trackEvent('FreeSupplier: First Data Added', { kind });
};

/** Public CBAM portal published (enabled) from /lite. */
export const trackFreeSupplierPortalPublished = () => {
  trackEvent('FreeSupplier: Portal Published');
};

/** Embed widget snippet copied. */
export const trackFreeSupplierWidgetCopied = ({ layout, theme } = {}) => {
  trackEvent('FreeSupplier: Widget Copied', { layout, theme });
};

/** An upsell CTA opened the upgrade modal / plans. */
export const trackFreeSupplierUpgradeClicked = ({ featureKey, source } = {}) => {
  trackEvent('FreeSupplier: Upgrade Clicked', { featureKey, source });
};

/**
 * Someone viewed a supplier's PUBLIC Supplier Page or widget (unauthenticated
 * — no Clerk user to identify). A commercial-acquisition signal per the S9
 * outcome (docs/features/free-supplier-journey/SLICES.md §S9); never carries
 * anything from the payload itself, only which surface and slug were opened.
 */
export const trackFreeSupplierPublicPageViewed = ({ surface, slug } = {}) => {
  if (!surface) {
    console.warn('⚠️ Cannot track FreeSupplier Public Page Viewed - missing surface');
    return;
  }
  trackEvent('FreeSupplier: Public Page Viewed', { surface, slug });
};

/** Declarant minted a supplier invite link (reusable or per-supplier). */
export const trackFreeSupplierInviteMinted = ({ kind } = {}) => {
  if (!kind) {
    console.warn('⚠️ Cannot track FreeSupplier Invite Minted - missing kind');
    return;
  }
  trackEvent('FreeSupplier: Invite Minted', { kind });
};

// Export mixpanel instance for advanced usage
export { mixpanel };

// Debug helper - expose to window for console debugging
if (typeof window !== 'undefined') {
  window.mixpanelDebug = {
    getCurrentUser: getCurrentMixpanelUser,
    identify: identifyUser,
    track: trackEvent,
    trackFreeSupplierInviteViewed,
    trackFreeSupplierSignupStarted,
    trackFreeSupplierOnboardingCompleted,
    trackFreeSupplierFirstDataAdded,
    trackFreeSupplierPortalPublished,
    trackFreeSupplierWidgetCopied,
    trackFreeSupplierUpgradeClicked,
    trackFreeSupplierInviteMinted,
    trackFreeSupplierPublicPageViewed,
    mixpanel,
  };
}

// Default export
export default {
  init: initMixpanel,
  identify: identifyUser,
  track: trackEvent,
  trackFreeTrialInitialization,
  trackWizardCompletion,
  trackTrialExpiredView,
  trackBookDemoClick,
  trackRequestExtensionClick,
  trackTrialExtensionModalOpened,
  trackEmailContentCopied,
  trackEmailClientOpened,
  trackFreeSupplierInviteViewed,
  trackFreeSupplierSignupStarted,
  trackFreeSupplierOnboardingCompleted,
  trackFreeSupplierFirstDataAdded,
  trackFreeSupplierPortalPublished,
  trackFreeSupplierWidgetCopied,
  trackFreeSupplierUpgradeClicked,
  trackFreeSupplierInviteMinted,
  trackFreeSupplierPublicPageViewed,
  getCurrentUser: getCurrentMixpanelUser,
};
